import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  ArrowPathIcon,
  Bars3Icon,
  Squares2X2Icon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  UserIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import { customersAPI, leadsAPI } from '../../services/api';
import { useCompany } from '../../contexts/CompanyContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const CustomerList = ({ onCreateCustomer, onEditCustomer, onViewCustomer, onDeleteCustomer, onBulkDelete, refreshTrigger }) => {
  const { companyData } = useCompany();
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [selectedCustomerType, setSelectedCustomerType] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    // Set default view to kanban on mobile devices
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768 ? 'kanban' : 'table';
    }
    return 'table';
  });
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  // Filter options
  const customerTypeOptions = [
    { value: 'individual', label: 'Individual' },
    { value: 'business', label: 'Business' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' }
  ];

  // Custom debounce hook
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  const debouncedSearchTermValue = useDebounce(searchTerm, 500); // 500ms delay

  useEffect(() => {
    fetchCustomers();
    fetchUsers();
  }, [currentPage, debouncedSearchTermValue, selectedStatus, selectedCustomerType, sortBy, sortOrder, refreshTrigger]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        search: debouncedSearchTermValue || undefined,
        status: selectedStatus || undefined,
        customerType: selectedCustomerType || undefined,
        sortBy,
        sortOrder
      };

      const response = await customersAPI.getAll(params);
      setCustomers(response.data.customers || []);
      setTotalCustomers(response.data.total || 0);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await leadsAPI.getAssignableUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleDeleteCustomer = (customer) => {
    onDeleteCustomer(customer);
  };

  const handleBulkDelete = () => {
    if (selectedCustomers.length === 0) return;
    onBulkDelete(selectedCustomers);
  };

  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomers(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedCustomers(
      selectedCustomers.length === customers.length
        ? []
        : customers.map(customer => customer._id)
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('active');
    setSelectedCustomerType('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  const renderTableView = () => (
    <div className="bg-white shadow-lg rounded-2xl border border-gray-200/50 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={selectedCustomers.length === customers.length && customers.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Lifetime Value
              </th>
              <th className="w-40 px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCustomers.map((customer) => (
              <motion.tr
                key={customer._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50 transition-all duration-200"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedCustomers.includes(customer._id)}
                    onChange={() => toggleCustomerSelection(customer._id)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-lg">
                          {customer.firstName?.charAt(0).toUpperCase() || '?'}
                          {customer.lastName?.charAt(0).toUpperCase() || ''}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col">
                        <h3 className="text-sm font-bold text-gray-900 truncate">
                          {customer.firstName} {customer.lastName}
                        </h3>
                        {customer.companyName && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1 self-start">
                            {customer.companyName}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <EnvelopeIcon className="h-4 w-4 mr-2 text-blue-500" />
                      <span className="text-gray-900 truncate">{customer.email}</span>
                    </div>
                    {customer.phone && (
                      <div className="flex items-center text-sm">
                        <PhoneIcon className="h-4 w-4 mr-2 text-green-500" />
                        <span className="text-gray-900">{customer.phone}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    customer.customerType === 'business'
                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                      : 'bg-green-100 text-green-800 border border-green-200'
                  }`}>
                    {customer.customerType === 'business' ? 'Business' : 'Individual'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(customer.status)}`}>
                    {customer.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-bold text-gray-900">
                    {formatCurrency(customer.lifetimeValue)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center justify-center space-x-1">
                    <motion.button
                      whileHover={{ scale: 1.1, backgroundColor: "#EEF2FF" }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onViewCustomer(customer._id)}
                      className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-indigo-200"
                      title="View Customer Details"
                    >
                      <EyeIcon className="h-3.5 w-3.5 mr-1" />
                      <span className="hidden sm:inline">View</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1, backgroundColor: "#EFF6FF" }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onEditCustomer(customer._id)}
                      className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-blue-200"
                      title="Edit Customer"
                    >
                      <PencilIcon className="h-3.5 w-3.5 mr-1" />
                      <span className="hidden sm:inline">Edit</span>
                    </motion.button>
                    {hasRole('company_admin') && (
                      <motion.button
                        whileHover={{ scale: 1.1, backgroundColor: "#FEF2F2" }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleDeleteCustomer(customer)}
                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-red-200"
                        title="Delete Customer"
                      >
                        <TrashIcon className="h-3.5 w-3.5 mr-1" />
                        <span className="hidden sm:inline">Delete</span>
                      </motion.button>
                    )}
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredCustomers.map((customer) => (
        <motion.div
          key={customer._id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden hover:shadow-xl transition-all duration-300"
        >
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">
                    {customer.firstName?.charAt(0).toUpperCase() || '?'}
                    {customer.lastName?.charAt(0).toUpperCase() || ''}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 truncate">
                    {customer.firstName} {customer.lastName}
                  </h3>
                  {customer.companyName && (
                    <p className="text-sm text-gray-600 truncate">{customer.companyName}</p>
                  )}
                </div>
              </div>
              <input
                type="checkbox"
                checked={selectedCustomers.includes(customer._id)}
                onChange={() => toggleCustomerSelection(customer._id)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex items-center text-sm">
                <EnvelopeIcon className="h-4 w-4 mr-2 text-blue-500" />
                <span className="text-gray-900 truncate">{customer.email}</span>
              </div>
              {customer.phone && (
                <div className="flex items-center text-sm">
                  <PhoneIcon className="h-4 w-4 mr-2 text-green-500" />
                  <span className="text-gray-900">{customer.phone}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mb-4">
              <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                customer.customerType === 'business'
                  ? 'bg-blue-100 text-blue-800 border border-blue-200'
                  : 'bg-green-100 text-green-800 border border-green-200'
              }`}>
                {customer.customerType === 'business' ? 'Business' : 'Individual'}
              </span>
              <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(customer.status)}`}>
                {customer.status}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="text-gray-600">Lifetime Value:</span>
                <div className="font-bold text-gray-900">{formatCurrency(customer.lifetimeValue)}</div>
              </div>
              <div className="flex items-center justify-center space-x-1">
                <motion.button
                  whileHover={{ scale: 1.1, backgroundColor: "#EEF2FF" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onViewCustomer(customer._id)}
                  className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-indigo-200"
                  title="View Customer Details"
                >
                  <EyeIcon className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">View</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1, backgroundColor: "#EFF6FF" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onEditCustomer(customer._id)}
                  className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-blue-200"
                  title="Edit Customer"
                >
                  <PencilIcon className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Edit</span>
                </motion.button>
                {hasRole('company_admin') && (
                  <motion.button
                    whileHover={{ scale: 1.1, backgroundColor: "#FEF2F2" }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDeleteCustomer(customer)}
                    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-red-200"
                    title="Delete Customer"
                  >
                    <TrashIcon className="h-3.5 w-3.5 mr-1" />
                    <span className="hidden sm:inline">Delete</span>
                  </motion.button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 border-green-200',
      inactive: 'bg-gray-100 text-gray-800 border-gray-200',
      suspended: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatCurrency = (amount) => {
    const currencyCode = companyData?.currency?.code || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filtered customers based on search (client-side filtering for additional search)
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      return (
        customer.firstName?.toLowerCase().includes(searchLower) ||
        customer.lastName?.toLowerCase().includes(searchLower) ||
        customer.email?.toLowerCase().includes(searchLower) ||
        customer.companyName?.toLowerCase().includes(searchLower) ||
        customer.phone?.includes(searchTerm)
      );
    });
  }, [customers, searchTerm]);

  if (loading && customers.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <>
     
      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg mb-6">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 flex-1 w-full lg:w-auto">
              {/* Search */}
              <div className="w-full sm:flex-1 sm:max-w-md">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 w-full"
                  />
                </div>
              </div>

              {/* Quick Filters */}
              <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                >
                  <option value="">All Status</option>
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedCustomerType}
                  onChange={(e) => setSelectedCustomerType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                >
                  <option value="">All Types</option>
                  {customerTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 whitespace-nowrap"
                >
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  More Filters
                  {showFilters ? (
                    <ChevronUpIcon className="h-4 w-4 ml-2" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 ml-2" />
                  )}
                </button>
              </div>

              {/* View Toggle */}
              <div className="flex items-center space-x-2 border-l border-gray-300 pl-4 ml-4">
                <button
                  onClick={() => setViewMode('table')}
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    viewMode === 'table'
                      ? 'text-indigo-700 bg-indigo-50 border border-indigo-200'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Bars3Icon className="h-4 w-4 mr-2" />
                  Table
                </button>
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    viewMode === 'kanban'
                      ? 'text-indigo-700 bg-indigo-50 border border-indigo-200'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Squares2X2Icon className="h-4 w-4 mr-2" />
                  Cards
                </button>
              </div>
            </div>
          </div>

          {/* Advanced Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4 pt-4 border-t border-gray-200"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Sort By */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="createdAt">Created Date</option>
                      <option value="firstName">First Name</option>
                      <option value="lastName">Last Name</option>
                      <option value="lifetimeValue">Lifetime Value</option>
                    </select>
                  </div>

                  {/* Sort Order */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sort Order
                    </label>
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="desc">Descending</option>
                      <option value="asc">Ascending</option>
                    </select>
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={clearFilters}
                      className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <XMarkIcon className="h-4 w-4 mr-2" />
                      Clear Filters
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {filteredCustomers.length > 0 && (
            <>
              {viewMode === 'table' ? renderTableView() : renderCardView()}
            </>
          )}

          {/* Empty State */}
          {filteredCustomers.length === 0 && !loading && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16"
            >
              <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6">
                <UserGroupIcon className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No customers found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || selectedStatus !== 'active' || selectedCustomerType
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by creating your first customer.'
                }
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onCreateCustomer}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create First Customer
              </motion.button>
            </motion.div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 mt-8">
              <div className="text-sm text-gray-700 text-center sm:text-left">
                Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, totalCustomers)} of {totalCustomers} customers
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-gray-700 bg-gray-50 rounded-xl">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CustomerList;